import React from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';

/**
 * Runtime ontology reference with optional domain label for UI display.
 */
interface OntologyRefForUI {
    readonly iri: string;
    readonly label: string;
    readonly domainLabel?: string;
}

type Props = {
    selectedOntologyIri: string | null;
    /** Ontologies already imported by the KG (shown first, at top) */
    kgOntologies?: OntologyRefForUI[];
    /** Ontologies from the BC's SemanticLayer (shown second) */
    contextOntologies: OntologyRefForUI[];
    /** All other domain ontologies (shown after separator) */
    domainOntologies: OntologyRefForUI[];
    onOntologyChange: (iri: string | null) => void;
    darkMode?: boolean;
};

type State = {
    isOpen: boolean;
    dropdownPosition: { top: number; left: number; width: number } | null;
};

/**
 * ModelingLanguageSelector - Per-diagram dropdown for selecting the modeling language (ontology).
 *
 * Shows two groups of ontologies:
 * 1. Context ontologies (from BC's SemanticLayer) - shown first
 * 2. Domain ontologies (from all domains) - shown after a separator
 *
 * The selected ontology determines which classes are shown in the Toolbox.
 */
export default class ModelingLanguageSelector extends React.Component<Props, State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private buttonRef = React.createRef<HTMLButtonElement>();

    state: State = {
        isOpen: false,
        dropdownPosition: null,
    };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent) => {
        if (this.containerRef.current && !this.containerRef.current.contains(event.target as Node)) {
            // Also check if click is inside the portal dropdown
            const dropdown = document.getElementById('modeling-language-selector-dropdown');
            if (dropdown && dropdown.contains(event.target as Node)) {
                return;
            }
            this.setState({ isOpen: false });
        }
    };

    handleToggle = () => {
        if (!this.state.isOpen && this.buttonRef.current) {
            const rect = this.buttonRef.current.getBoundingClientRect();
            this.setState({
                isOpen: true,
                dropdownPosition: {
                    top: rect.bottom + 4,
                    left: rect.left,
                    width: rect.width,
                },
            });
        } else {
            this.setState({ isOpen: false });
        }
    };

    handleSelect = (iri: string | null) => {
        this.props.onOntologyChange(iri);
        this.setState({ isOpen: false });
    };

    renderOntologyItem = (ontology: OntologyRefForUI) => {
        const { selectedOntologyIri } = this.props;
        const isSelected = ontology.iri === selectedOntologyIri;
        return (
            <div
                key={ontology.iri}
                className={clsx(
                    "px-3 py-2.5 text-[13px] cursor-pointer transition-all duration-100 flex items-center gap-2",
                    isSelected
                        ? "bg-accent-teal/10 dark:bg-accent/15 text-accent-teal dark:text-accent font-semibold"
                        : "text-slate-700 dark:text-white/85 bg-transparent hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10"
                )}
                onClick={() => this.handleSelect(ontology.iri)}
            >
                {isSelected && (
                    <svg width="14" height="14" viewBox="0 0 14 14">
                        <path d="M2,7 L5,10 L12,3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
                {!isSelected && <div className="w-3.5" />}
                <span className="flex items-center gap-2">
                    {ontology.label}
                    {ontology.domainLabel && (
                        <span className="text-[11px] text-slate-400 dark:text-white/40">
                            ({ontology.domainLabel})
                        </span>
                    )}
                </span>
            </div>
        );
    };

    render() {
        const { selectedOntologyIri, kgOntologies = [], contextOntologies, domainOntologies } = this.props;
        const { isOpen, dropdownPosition } = this.state;

        // Find selected ontology in any group
        const allOntologies = [...kgOntologies, ...contextOntologies, ...domainOntologies];
        const selectedOntology = allOntologies.find(o => o.iri === selectedOntologyIri);

        const dropdown = isOpen && dropdownPosition ? (
            <div
                id="modeling-language-selector-dropdown"
                className="fixed bg-surface-light dark:bg-[rgba(28,30,36,0.98)] border border-slate-200 dark:border-accent-border/20 rounded-lg shadow-[0_8px_32px_rgba(100,120,140,0.15)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden z-[10001] backdrop-blur-[12px] max-h-[400px] overflow-y-auto"
                style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    minWidth: dropdownPosition.width,
                }}
            >
                {/* None option */}
                <div
                    className={clsx(
                        "px-3 py-2.5 text-[13px] cursor-pointer transition-all duration-100 flex items-center gap-2",
                        selectedOntologyIri === null
                            ? "bg-accent-teal/10 dark:bg-accent/15 text-accent-teal dark:text-accent font-semibold"
                            : "text-slate-500 dark:text-white/60 bg-transparent hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10"
                    )}
                    onClick={() => this.handleSelect(null)}
                >
                    {selectedOntologyIri === null && (
                        <svg width="14" height="14" viewBox="0 0 14 14">
                            <path d="M2,7 L5,10 L12,3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                    {selectedOntologyIri !== null && <div className="w-3.5" />}
                    <span className="italic">None</span>
                </div>

                {/* KG ontologies (already imported by the knowledge graph) */}
                {kgOntologies.length > 0 && (
                    <>
                        <div className="border-t border-slate-200 dark:border-accent-border/20 my-1" />
                        <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-white/40">
                            KG Ontologies
                        </div>
                        {kgOntologies.map(this.renderOntologyItem)}
                    </>
                )}

                {/* Context ontologies (from BC's SemanticLayer) */}
                {contextOntologies.length > 0 && (
                    <>
                        <div className="border-t border-slate-200 dark:border-accent-border/20 my-1" />
                        <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-white/40">
                            Context Ontologies
                        </div>
                        {contextOntologies.map(this.renderOntologyItem)}
                    </>
                )}

                {/* Domain ontologies (from all domains) */}
                {domainOntologies.length > 0 && (
                    <>
                        <div className="border-t border-slate-200 dark:border-accent-border/20 my-1" />
                        <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-white/40">
                            Domain Ontologies
                        </div>
                        {domainOntologies.map(this.renderOntologyItem)}
                    </>
                )}
            </div>
        ) : null;

        return (
            <div ref={this.containerRef} className="relative inline-block ml-3 font-sans">
                <button
                    ref={this.buttonRef}
                    className={clsx(
                        "flex items-center gap-2 px-3 py-2 text-[13px] font-medium rounded-lg border cursor-pointer outline-none transition-all duration-150 min-w-[180px] justify-between",
                        isOpen
                            ? "bg-slate-50 dark:bg-accent/10 border-accent-teal dark:border-accent/40 text-slate-700 dark:text-white"
                            : "bg-white dark:bg-white/[0.06] border-slate-200 dark:border-accent-border text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-accent/10 hover:border-accent-teal dark:hover:border-accent/40"
                    )}
                    onClick={this.handleToggle}
                    title="Select modeling language (ontology)"
                >
                    <span>{selectedOntology?.label || 'Select Language'}</span>
                    <svg
                        className={clsx(
                            "transition-transform duration-200 opacity-60",
                            isOpen && "rotate-180"
                        )}
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                    >
                        <path d="M3,4 L6,7 L9,4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                {dropdown && ReactDOM.createPortal(dropdown, document.body)}
            </div>
        );
    }
}
